import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import SEO from "../components/SEO";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { Bell, CheckCheck, ChevronRight } from "lucide-react";

const ago = (iso) => {
  const m = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (m < 1) return "just now"; if (m < 60) return `${m}m ago`; const h = Math.round(m / 60); if (h < 24) return `${h}h ago`; return `${Math.round(h / 24)}d ago`;
};

export default function Notifications() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  const load = () => api.get("/notifications/inbox", { params: { limit: 100 } }).then((r) => setData(r.data)).catch(() => setData({ items: [], unread: 0 }));
  useEffect(() => { if (!loading && !user) navigate("/auth?mode=login"); if (user) load(); /* eslint-disable-next-line */ }, [user, loading]);

  const markAll = async () => { await api.post("/notifications/inbox/read", { all: true }); load(); window.dispatchEvent(new Event("nexus-inbox-read")); };
  const open = async (n) => {
    if (!n.read) { await api.post("/notifications/inbox/read", { ids: [n.id] }); window.dispatchEvent(new Event("nexus-inbox-read")); }
    if (n.url) navigate(n.url);
  };

  return (
    <div className="min-h-screen grain">
      <SEO title="Notifications · Nexus" description="Your Nexus updates." path="/notifications" />
      <Navbar />
      <div className="max-w-3xl mx-auto px-6 lg:px-10 py-10 space-y-6" data-testid="notifications-page">
        <div className="flex items-end justify-between gap-3 flex-wrap">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-3 flex items-center gap-2"><Bell className="h-3.5 w-3.5 text-nexus-pink" /> Inbox</div>
            <h1 className="font-display font-bold text-4xl md:text-5xl tracking-tighter">Notifications</h1>
          </div>
          {data?.unread > 0 && (
            <button onClick={markAll} className="inline-flex items-center gap-1.5 text-xs text-zinc-300 hover:text-white" data-testid="notifications-mark-all">
              <CheckCheck className="h-4 w-4" /> Mark all as read ({data.unread})
            </button>
          )}
        </div>
        {!data ? <div className="text-zinc-500">Loading…</div> : data.items.length === 0 ? (
          <div className="glass rounded-2xl p-12 text-center text-zinc-500" data-testid="notifications-empty">Nothing yet — offers, messages, approvals and announcements from Nexus will land here.</div>
        ) : (
          <div className="space-y-2" data-testid="notifications-list">
            {data.items.map((n) => (
              <button key={n.id} onClick={() => open(n)} className={`w-full text-left flex items-start gap-3 p-4 rounded-2xl hairline transition-colors hover:bg-white/[0.04] ${n.read ? "bg-transparent" : "bg-white/[0.03]"}`} data-testid={`notification-${n.id}`} data-read={n.read ? "1" : "0"}>
                <span className={`mt-2 h-2 w-2 rounded-full shrink-0 ${n.read ? "bg-transparent" : "nexus-gradient"}`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-3">
                    <div className={`text-sm truncate ${n.read ? "text-zinc-300" : "text-white font-semibold"}`}>{n.title}</div>
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500 shrink-0">{ago(n.created_at)}</div>
                  </div>
                  <div className="text-xs text-zinc-400 mt-0.5 whitespace-pre-wrap">{n.body}</div>
                  {n.type === "broadcast" && <div className="text-[10px] uppercase tracking-widest text-nexus-pink mt-1">From the Nexus team</div>}
                </div>
                {n.url && <ChevronRight className="h-4 w-4 text-zinc-600 shrink-0 mt-1" />}
              </button>
            ))}
          </div>
        )}
        <div className="text-xs text-zinc-500">Control which of these also reach your email in <Link to="/settings#emails" className="text-nexus-blue hover:underline">Settings → Email notifications</Link>.</div>
      </div>
    </div>
  );
}
